import { Component, ReactNode } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';
import { RefreshCw, Plane, Home } from 'lucide-react';
import { Sentry } from '@/lib/analytics';

// Font definitions
const MONTSERRAT = { fontFamily: "'Montserrat', sans-serif", fontStyle: 'normal' as const };
const RALEWAY = { fontFamily: "'Raleway', sans-serif", fontStyle: 'normal' as const };

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: { componentStack?: string } | null;
}

/**
 * Class-based Error Boundary for catching React errors
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: { componentStack?: string }) {
    this.setState({ errorInfo });
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          backgroundColor: '#FBF9F6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 32px',
              borderRadius: '50%',
              backgroundColor: 'rgba(65,47,33,0.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Plane size={40} color="rgba(65,47,33,0.5)" strokeWidth={1.5} />
            </div>

            <h1 style={{
              ...MONTSERRAT,
              fontWeight: 400,
              fontSize: '28px',
              color: '#412F21',
              marginBottom: '16px',
              letterSpacing: '-0.01em'
            }}>
              Our Engineers Are Travelling
            </h1>

            <p style={{
              ...RALEWAY,
              fontWeight: 300,
              fontSize: '15px',
              color: 'rgba(65,47,33,0.65)',
              lineHeight: '1.7',
              marginBottom: '36px',
              maxWidth: '420px',
              margin: '0 auto 36px'
            }}>
              We'll get back to this soon. In the meantime, please reload the page or head back home.
            </p>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={this.handleReload}
                style={{
                  ...MONTSERRAT,
                  fontWeight: 500,
                  fontSize: '13px',
                  padding: '14px 28px',
                  backgroundColor: 'rgba(65,47,33,0.9)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  letterSpacing: '0.01em'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(65,47,33,1)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(65,47,33,0.9)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <RefreshCw size={16} />
                Reload Page
              </button>

              <button
                onClick={this.handleGoHome}
                style={{
                  ...MONTSERRAT,
                  fontWeight: 500,
                  fontSize: '13px',
                  padding: '14px 28px',
                  backgroundColor: 'white',
                  color: 'rgba(65,47,33,0.9)',
                  border: '1px solid rgba(65,47,33,0.15)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                  letterSpacing: '0.01em'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(65,47,33,0.04)';
                  e.currentTarget.style.borderColor = 'rgba(65,47,33,0.25)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.borderColor = 'rgba(65,47,33,0.15)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <Home size={16} />
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Route Error Component for React Router errors
 */
export function RouteErrorBoundary() {
  const error = useRouteError();

  const is404 = isRouteErrorResponse(error) && error.status === 404;

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#FBF9F6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 32px',
          borderRadius: '50%',
          backgroundColor: 'rgba(65,47,33,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Plane size={40} color="rgba(65,47,33,0.5)" strokeWidth={1.5} />
        </div>

        <h1 style={{
          ...MONTSERRAT,
          fontWeight: 400,
          fontSize: '28px',
          color: '#412F21',
          marginBottom: '16px',
          letterSpacing: '-0.01em'
        }}>
          {is404 ? 'Lost in Transit' : 'Our Engineers Are Travelling'}
        </h1>

        <p style={{
          ...RALEWAY,
          fontWeight: 300,
          fontSize: '15px',
          color: 'rgba(65,47,33,0.65)',
          lineHeight: '1.7',
          marginBottom: '36px',
          maxWidth: '420px',
          margin: '0 auto 36px'
        }}>
          {is404
            ? "The page you're looking for seems to have taken a detour. Let's get you back on track."
            : "We'll get back to this soon. In the meantime, please reload the page or head back home."}
        </p>

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {!is404 && (
            <button
              onClick={handleReload}
              style={{
                ...MONTSERRAT,
                fontWeight: 500,
                fontSize: '13px',
                padding: '14px 28px',
                backgroundColor: 'rgba(65,47,33,0.9)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                letterSpacing: '0.01em'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(65,47,33,1)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(65,47,33,0.9)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <RefreshCw size={16} />
              Reload Page
            </button>
          )}

          <button
            onClick={handleGoHome}
            style={{
              ...MONTSERRAT,
              fontWeight: 500,
              fontSize: '13px',
              padding: '14px 28px',
              backgroundColor: is404 ? 'rgba(65,47,33,0.9)' : 'white',
              color: is404 ? 'white' : 'rgba(65,47,33,0.9)',
              border: is404 ? 'none' : '1px solid rgba(65,47,33,0.15)',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              letterSpacing: '0.01em'
            }}
            onMouseEnter={(e) => {
              if (is404) {
                e.currentTarget.style.backgroundColor = 'rgba(65,47,33,1)';
              } else {
                e.currentTarget.style.backgroundColor = 'rgba(65,47,33,0.04)';
                e.currentTarget.style.borderColor = 'rgba(65,47,33,0.25)';
              }
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              if (is404) {
                e.currentTarget.style.backgroundColor = 'rgba(65,47,33,0.9)';
              } else {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = 'rgba(65,47,33,0.15)';
              }
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Home size={16} />
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
}
